/**
 * Decision 0144 (experiment 11, 0136 §1): what a ref chip and its preview show, for every ref type. One function for the
 * web inside a project (from its live snapshot) and the cloud's ref lookup (Home, the bell): type, variant, title, one
 * status mark, the status in words, the page it opens and the facts the preview lists; a card's shape for `P-n` (and
 * for a decided card's `D-n`).
 *
 * Marks (README §3): waits on you (needs), running (live), blocked / failed / stale / away (blocked), open and not
 * started (wait), done / passed / merged (done), cancelled / withdrawn (gone), nothing worth saying (none). Pure.
 */
import { ITEM_TYPES, type RefView } from "@minga/protocol"
import { itemHolds, itemRank } from "./brief-sections"
import { cardShape } from "./card-shape"
import { askRank } from "./needs-you"
import { artifactByRef } from "./outputs"
import { isOpenProposal, isRunEnded } from "./state"
import type { Comment, Item, ProjectState, Proposal, Run } from "./types"

/** What the caller knows beyond the state (the cloud's projections; the web's decided index). All optional. */
export interface RefViewExtras {
  /** A decided card compacted out of the state, by its P-n. */
  readonly decidedByRef?: (ref: string) => Proposal | null | undefined
  /** A decided card by its decision ref (D-n). */
  readonly decidedByDecisionRef?: (ref: string) => Proposal | null | undefined
  /** A decision comment by its ref (D-n). */
  readonly decisionComment?: (ref: string) => Comment | null | undefined
}

const ITEM_STATUS_WORDS: Record<Item["status"], string> = {
  triage: "Triage",
  backlog: "Planning",
  todo: "Ready",
  in_progress: "In progress",
  in_review: "In review",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
}

const typeLabel = (key: string): string => ITEM_TYPES.find((t) => t.key === key)?.label ?? "Item"

const memberName = (s: ProjectState, userId: string, viewer: string | null): string =>
  userId === viewer ? "you" : (s.members.find((m) => m.userId === userId)?.displayName ?? "a person")

const agentName = (s: ProjectState, agentId: string): string =>
  s.agents.find((a) => a.id === agentId)?.name ?? "an agent"

const liveRunOf = (s: ProjectState, itemId: string): Run | undefined =>
  s.runs.find((r) => r.itemId === itemId && !isRunEnded(r) && r.kind !== "turn")

const dayText = (at: number): string =>
  new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })

const itemView = (s: ProjectState, t: Item, viewer: string | null, base: string): RefView => {
  const live = liveRunOf(s, t.id)
  const forViewer = t.assignee?._tag === "user" && t.assignee.userId === viewer
  const [mark, status]: [RefView["mark"], string] =
    t.status === "done"
      ? ["done", "Done"]
      : t.status === "cancelled"
        ? ["gone", "Cancelled"]
        : t.status === "blocked"
          ? ["blocked", "Blocked"]
          : live
            ? ["live", "Running"]
            : forViewer
              ? ["needs", `${ITEM_STATUS_WORDS[t.status]}, waiting on you`]
              : t.status === "in_progress" || t.status === "in_review"
                ? ["wait", ITEM_STATUS_WORDS[t.status]]
                : ["wait", ITEM_STATUS_WORDS[t.status]]
  const owner =
    t.owner?._tag === "agent"
      ? agentName(s, t.owner.agentId)
      : t.owner?._tag === "user"
        ? memberName(s, t.owner.userId, viewer)
        : null
  const assignee =
    t.assignee?._tag === "user"
      ? memberName(s, t.assignee.userId, viewer)
      : t.assignee?._tag === "agent"
        ? agentName(s, t.assignee.agentId)
        : t.assignee?._tag === "worker"
          ? "a worker"
          : null
  const holds = itemHolds(s, t)
  const facts = [
    ...(owner ? [{ label: "Owner", value: owner }] : []),
    ...(assignee && assignee !== owner ? [{ label: "Doing it", value: assignee }] : []),
    ...(holds > 0 ? [{ label: "Blocks", value: `${holds} ${holds === 1 ? "item" : "items"}` }] : []),
    ...(t.dueAt ? [{ label: "Due", value: dayText(t.dueAt) }] : []),
  ]
  return {
    ref: t.ref,
    type: "item",
    variant: typeLabel(t.type),
    title: t.title,
    status,
    mark,
    href: `${base}/i/${encodeURIComponent(t.ref)}`,
    projectName: s.project?.name ?? "",
    facts,
    rank: itemRank(s, t),
    holds,
  }
}

const cardView = (
  s: ProjectState,
  p: Proposal,
  viewer: string | null,
  base: string,
  ref: string,
): RefView => {
  const shape = cardShape(s, p, viewer)
  const open = isOpenProposal(p)
  const [mark, status]: [RefView["mark"], string] = open
    ? shape.waitsOnViewer
      ? ["needs", "Waiting on you"]
      : ["wait", `Waiting on ${shape.waitsOn}`]
    : p.state === "withdrawn" || p.state === "expired"
      ? ["gone", p.state === "withdrawn" ? "Withdrawn" : "Expired"]
      : ["done", shape.decided?.line ?? "Decided"]
  const facts = [
    ...(shape.from ? [{ label: "From", value: shape.from }] : []),
    ...(shape.blocks > 0
      ? [{ label: "Blocks", value: `${shape.blocks} ${shape.blocks === 1 ? "item" : "items"}` }]
      : []),
    // the recommendation shows as its own line under the facts (the preview and the popover)
    ...(p.decisionRef && ref !== p.decisionRef ? [{ label: "Decision", value: p.decisionRef }] : []),
  ]
  return {
    ref,
    type: ref.startsWith("D-") ? "decision" : "card",
    variant: shape.kindWord,
    title: shape.ask,
    status,
    mark,
    href: open
      ? `${base}?view=needs&card=${encodeURIComponent(p.id)}`
      : `${base}?decision=${encodeURIComponent(p.id)}`,
    projectName: s.project?.name ?? "",
    facts,
    card: shape,
    rank: askRank(s, p),
    holds: shape.blocks,
  }
}

const RUN_PHASE_WORDS: Record<string, string> = {
  preparing: "Preparing",
  running: "Running",
  validating: "Checking",
  awaiting_permission: "Waits for a permission",
  finishing: "Finishing",
}

const runView = (s: ProjectState, r: Run, ref: string, base: string): RefView => {
  const item = r.itemId ? s.items.find((t) => t.id === r.itemId) : undefined
  const validator = r.kind === "validator" || ref.startsWith("V-")
  let mark: RefView["mark"]
  let status: string
  if (r.state._tag === "ended") {
    const ok = r.state.stopReason === "completed"
    const failed = r.verdict && "_tag" in r.verdict && r.verdict._tag === "fail"
    mark = ok && !failed ? "done" : "blocked"
    status = ok
      ? failed
        ? "Failed its check"
        : "Ended"
      : `Stopped: ${r.state.stopReason.replace(/_/g, " ")}`
  } else if (r.state._tag === "leased") {
    const phase = r.state.phase
    mark = phase === "awaiting_permission" ? "needs" : "live"
    status = RUN_PHASE_WORDS[phase] ?? "Running"
  } else {
    mark = "wait"
    status = r.state._tag === "waiting" ? "Waiting for a runner" : "Queued"
  }
  const agent = r.agentId ? agentName(s, r.agentId) : null
  return {
    ref,
    type: validator ? "validator" : "run",
    variant: validator
      ? "Validator run"
      : r.kind === "turn"
        ? "Turn"
        : r.kind === "publish"
          ? "Publish run"
          : "Worker run",
    title: item ? `${item.ref} ${item.title}` : agent ? `${agent}'s turn` : ref,
    status,
    mark,
    href: `${base}?run=${encodeURIComponent(r.id)}`,
    projectName: s.project?.name ?? "",
    facts: [
      ...(agent ? [{ label: "Agent", value: agent }] : []),
      ...(item ? [{ label: "Item", value: item.ref }] : []),
    ],
  }
}

const KIND_WORDS: Record<string, string> = {
  pull_request: "Pull request",
  page_draft: "Page draft",
  report: "Report",
  document: "Document",
  brief: "Brief",
}

const CADENCE_MS = { hourly: 3_600_000, daily: 86_400_000, weekly: 7 * 86_400_000 } as const

/**
 * The view of one ref, or null when nothing here has that ref ("GPT-5" stays text). `base` is the project's app path
 * (`/w/<ws>/p/<project>`); `now` decides a KPI's freshness.
 */
export const refView = (
  s: ProjectState,
  ref: string,
  viewer: string | null,
  base: string,
  now: number,
  extras: RefViewExtras = {},
): RefView | null => {
  const r = ref.trim()
  const upper = r.toUpperCase()
  const project = s.project?.name ?? ""
  const item = s.items.find((t) => t.ref.toUpperCase() === upper)
  if (item) return itemView(s, item, viewer, base)
  const m = /^([A-Z])-(\d+)$/.exec(r)
  if (!m) return null
  switch (m[1]) {
    case "P": {
      const p = s.proposals.find((x) => x.ref === r) ?? extras.decidedByRef?.(r) ?? undefined
      return p ? cardView(s, p, viewer, base, r) : null
    }
    case "D": {
      const p = s.proposals.find((x) => x.decisionRef === r) ?? extras.decidedByDecisionRef?.(r) ?? undefined
      if (p) return cardView(s, p, viewer, base, r)
      const c = extras.decisionComment?.(r)
      if (!c) return null
      const target = c.target._tag === "item" ? s.items.find((t) => t.id === c.target.id) : undefined
      const goal = c.target._tag === "goal" ? s.goals.find((g) => g.id === c.target.id) : undefined
      const by =
        c.createdBy._tag === "agent"
          ? agentName(s, c.createdBy.agentId)
          : c.createdBy._tag === "user"
            ? memberName(s, c.createdBy.userId, viewer)
            : null
      return {
        ref: r,
        type: "decision",
        variant: "Decision",
        title: (c.body.split("\n").find((l) => l.trim() !== "") ?? r)
          .replace(/\*\*/g, "")
          .trim()
          .slice(0, 200),
        status: `Decided ${dayText(c.createdAt)}`,
        mark: "none",
        href: target
          ? `${base}/i/${encodeURIComponent(target.ref)}`
          : goal
            ? `${base}/g/${encodeURIComponent(goal.ref)}`
            : base,
        projectName: project,
        facts: [
          ...(by ? [{ label: "By", value: by }] : []),
          ...(target ? [{ label: "On", value: target.ref }] : goal ? [{ label: "On", value: goal.ref }] : []),
        ],
      }
    }
    case "R":
    case "W":
    case "V": {
      const run = s.runs.find((x) => (m[1] === "R" ? x.ref === r : x.worker?.ref === r))
      return run ? runView(s, run, r, base) : null
    }
    case "O": {
      const a = artifactByRef({ artifacts: s.artifacts }, r)
      if (!a) return null
      const [mark, status]: [RefView["mark"], string] =
        a.state === "rejected"
          ? ["gone", "Rejected"]
          : a.state === "draft"
            ? ["wait", "Draft"]
            : [
                a.pr?.snapshot.state === "merged" ? "done" : "none",
                a.state === "published" ? "Published" : "Final",
              ]
      return {
        ref: r,
        type: "output",
        variant: a.doc
          ? "Document"
          : (KIND_WORDS[a.kind] ?? a.kind.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())),
        title: a.title,
        status,
        mark,
        href: `${base}/o/${encodeURIComponent(r)}`,
        projectName: project,
        facts: [],
      }
    }
    case "G": {
      const g = s.goals.find((x) => x.ref === r)
      if (!g) return null
      const [mark, status]: [RefView["mark"], string] =
        g.status === "achieved"
          ? ["done", "Achieved"]
          : g.status === "missed"
            ? ["blocked", "Missed"]
            : g.status === "abandoned"
              ? ["gone", "Abandoned"]
              : ["none", "Active"]
      return {
        ref: r,
        type: "goal",
        variant: "Goal",
        title: g.title,
        status,
        mark,
        href: `${base}/g/${encodeURIComponent(r)}`,
        projectName: project,
        facts: [],
      }
    }
    case "K": {
      const k = s.kpis.find((x) => x.ref === r)
      if (!k) return null
      const stale = !k.latest || now - k.latest.at > 2 * CADENCE_MS[k.cadence]
      return {
        ref: r,
        type: "kpi",
        variant: "KPI",
        title: k.name,
        status: k.latest
          ? `${k.latest.value}${k.unit ? ` ${k.unit}` : ""} on ${dayText(k.latest.at)}${stale ? ", no newer number" : ""}`
          : "No number yet",
        mark: stale ? "blocked" : "none",
        href: `${base}/k/${encodeURIComponent(r)}`,
        projectName: project,
        facts: [{ label: "Cadence", value: k.cadence }],
      }
    }
    case "M": {
      const mem = s.memory.find((x) => x.ref === r)
      if (!mem) return null
      return {
        ref: r,
        type: "memory",
        variant: "Memory",
        title: mem.title,
        status: mem.status === "accepted" ? "Kept" : mem.status === "archived" ? "Archived" : "Revoked",
        mark: mem.status === "accepted" ? "none" : "gone",
        href: `${base}/m/${encodeURIComponent(r)}`,
        projectName: project,
        facts: [],
      }
    }
    default:
      return null
  }
}
